<script setup lang="ts">
import type { IHistogramItem } from "~/types/IImpactV3Histograms";
import type { IRTImpactCharts } from "./IRTImpactCharts";
import type { ChartViewTypes } from "~/components/common/types";
import { getOrdinalSuffix } from "~/helpers/getDayOrdinalSuffix";
import { ChartPeriodEnum } from "~/enums";
import ImpactNoData from "../Impact/ImpactNoData.vue";
import { numberCommaSeparator } from "~/helpers";

const props = defineProps<IRTImpactCharts>();

function getCategories(): string[] {
  if (!props.chartData) return [];
  return props.chartData.histogramItems.map((m: IHistogramItem) => {
    const isLastMonth = props.chartPeriod === ChartPeriodEnum.lastMonth;
    const isSinceBeginnig = props.chartPeriod === ChartPeriodEnum.beginning;

    const date = new Date(m.dateKey);
    const ordinalSuffix = isLastMonth ? getOrdinalSuffix(date.getDate()) : "";

    const formattedDate = date.toLocaleDateString("en-US", {
      month: !isSinceBeginnig ? "short" : undefined,
      day: isLastMonth ? "numeric" : undefined,
      year: isSinceBeginnig ? "numeric" : undefined
    });

    return `${formattedDate}${ordinalSuffix}`;
  });
}

function convertCamelCase(str: string) {
  const result = str.replace(/([A-Z])/g, " $1").toLowerCase();
  return result.charAt(0).toUpperCase() + result.slice(1);
}

const chartSeries = computed(() => {
  const series = [];
  for (const chartSerie in props.chartSeriesEnabled) {
    const customData = getCustomData(chartSerie);

    series.push({
      name: chartSerie,
      data: customData.data,
      color: customData.color,
      visible: props.chartSeriesEnabled[chartSerie as keyof ChartViewTypes],
      marker: {
        lineColor: customData.color
      }
    });
  }

  return series;
});

const chartOptions = computed(() => {
  return {
    chart: {
      type: props.chartMode,
      inverted: false
    },
    title: {
      text: ""
    },
    plotOptions: {
      series: {
        animationLimit: 1,
        showInLegend: false,
        lineWidth: 4,
        marker: {
          radius: 6,
          symbol: "circle",
          fillColor: "#ffffff",
          lineWidth: 2
        }
      },
      column: {
        grouping: false,
        stacking: "normal"
      },
      spline: {
        stickyTracking: false
      }
    },
    tooltip: {
      formatter: function () {
        return `<span style="font-size: 12px">${convertCamelCase(
          this.series.name
        )}</span><br /><strong>${numberCommaSeparator(this.y)}</strong>`;
      },
      backgroundColor: "#282828",
      color: "white",
      style: {
        color: "#FFFFFF",
        lineHeight: "20px"
      }
    },
    xAxis: {
      categories: getCategories(),
      labels: {
        rotation: 270,
        style: {
          fontSize: "14px",
          color: "#6B6B6B"
        }
      }
    },
    yAxis: {
      title: {
        text: ""
      },
      labels: {
        format: "{text}"
      },
      min: 0
    },
    series: chartSeries.value
  };
});

function getCustomData(opt: string) {
  if (!props.chartData) return { color: "", data: [] };

  const getData = (dataName: keyof IHistogramItem): (string | number)[] => {
    return (
      props.chartData?.histogramItems.map(
        (item: IHistogramItem) => item[dataName]
      ) || []
    );
  };

  switch (opt) {
    case "totalViews":
      return {
        color: "#003BDE",
        data: getData("totalViewsCount")
      };
    case "articleViews":
      return {
        color: "#E87626",
        data: getData("articleViewsCount")
      };
    case "articleDownloads":
      return {
        color: "#6C51DC",
        data: getData("articleDownloadsCount")
      };
    case "topicViews":
      return {
        color: "#65C161",
        data: getData("topicViewsCount")
      };
    default:
      return {
        color: "#e87626",
        data: getData("totalViewsCount")
      };
  }
}
</script>
<template>
  <div
    class="RTImpactCharts"
    :class="{ 'RTImpactCharts--noData': !chartData?.totalViewsCount }">
    <highchart
      v-if="chartData?.totalViewsCount"
      :modules="['accessibility']"
      :options="chartOptions" />
    <ImpactNoData v-else />
  </div>
</template>
<style lang="scss">
.RTImpactCharts {
  padding-top: 48px;

  .highcharts-axis-labels {
    font-family: "MuseoSans";
    font-weight: 300;
  }

  .highcharts-axis-line {
    stroke: rgb(230, 230, 230);
  }

  .highcharts-credits {
    display: none;
  }
}
</style>
